/**
 * CostByAccountCard — custo por conta AWS (SP3).
 *
 * BarChart Mantine na paleta teal do DS (`inovareTeal`). Cada barra é clicável
 * (`onSelect(key)`) para drill-down/cross-filter (RF4). Estado vazio → EmptyState
 * `bare`. Selo de dados parciais quando `is_partial`.
 *
 * Não faz fetch: recebe `BreakdownResponse` por prop (hooks ficam nas telas
 * das Tasks 12-14). Cores apenas via tokens DS/Mantine.
 */
import { Box } from '@mantine/core'
import { BarChart } from '@mantine/charts'
import { IconBuildingBank } from '@tabler/icons-react'

import { WidgetCard, CardHeader, EmptyState } from '@/components/ui'
import { formatUSD } from '@/utils/finops'
import type { BreakdownResponse } from '@/api/finopsDashboard'

function compactUSD(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}k`
  return `$${value.toFixed(0)}`
}

export interface CostByAccountCardProps {
  data: BreakdownResponse
  /** Drill-down: chamado com a `key` da conta clicada (RF4). */
  onSelect?: (key: string) => void
  /** Altura do gráfico em px. Default 260. */
  height?: number
  title?: string
}

export function CostByAccountCard({
  data,
  onSelect,
  height = 260,
  title = 'Custo por conta AWS',
}: CostByAccountCardProps) {
  const chartData = data.items.map((it) => ({
    account: it.key,
    Custo: it.amount_usd,
  }))

  return (
    <WidgetCard accent="primary" p={0}>
      <CardHeader
        title={title}
        badge={data.is_partial ? { value: 'Parcial', color: 'inovareTeal' } : undefined}
      />
      <Box p="md">
        {chartData.length === 0 ? (
          <EmptyState
            icon={IconBuildingBank}
            title="Sem dados de contas"
            description="Não há custo por conta no escopo e período selecionados."
            accent="info"
            size="sm"
            bare
          />
        ) : (
          <BarChart
            h={height}
            data={chartData}
            dataKey="account"
            orientation="horizontal"
            series={[{ name: 'Custo', color: 'inovareTeal.7' }]}
            tickLine="xy"
            gridAxis="x"
            valueFormatter={(v) => compactUSD(Number(v))}
            withTooltip
            tooltipProps={{
              content: ({ payload }) => {
                const p = payload?.[0]
                if (!p) return null
                return (
                  <Box
                    p="xs"
                    style={{
                      background: 'var(--mantine-color-body)',
                      border: '1px solid var(--ai-border-light)',
                      borderRadius: 'var(--mantine-radius-sm)',
                    }}
                  >
                    {String(p.payload.account)}: {formatUSD(Number(p.value))}
                  </Box>
                )
              },
            }}
            barProps={
              onSelect
                ? {
                    cursor: 'pointer',
                    onClick: (entry: { account?: string }) => {
                      if (entry?.account) onSelect(entry.account)
                    },
                  }
                : undefined
            }
          />
        )}
      </Box>
    </WidgetCard>
  )
}
